<script setup>
import { ref } from 'vue'

const props = defineProps({
  linhas: { type: Array, required: true },
  nivel: { type: Number, default: 1 },
  isReadOnly: { type: Boolean, default: false },
  listas: { type: Object, default: () => ({}) },
  expandidos: { type: Set, default: () => new Set() }
})

const emit = defineEmits([
  'adicionar-filho',
  'editar',
  'excluir',
  'mover',
  'update:expandidos'
])

// --- Expand/Colapse ---
const isExpanded = (id) => props.expandidos.has(id)

const toggleExpand = (id) => {
  const novoSet = new Set(props.expandidos)
  if (novoSet.has(id)) {
    novoSet.delete(id)
  } else {
    novoSet.add(id)
  }
  emit('update:expandidos', novoSet)
}

// --- Drag-and-Drop ---
const draggingId = ref(null)
const dragOverId = ref(null)

const onDragStart = (event, linha) => {
  draggingId.value = linha.idDreLinha
  event.dataTransfer.effectAllowed = 'move'
  event.dataTransfer.setData('text/plain', String(linha.idDreLinha))
}

const onDragOver = (event, linha) => {
  event.preventDefault()
  dragOverId.value = linha.idDreLinha
}

const onDragLeave = (event) => {
  const relatedTarget = event.relatedTarget
  const currentNode = event.currentTarget
  if (currentNode && relatedTarget && currentNode.contains(relatedTarget)) {
    return
  }
  dragOverId.value = null
}

const onDrop = (event, linhaDestino) => {
  event.preventDefault()
  dragOverId.value = null
  const itemId = Number(event.dataTransfer.getData('text/plain'))
  if (!itemId || itemId === linhaDestino.idDreLinha) {
    draggingId.value = null
    return
  }
  emit('mover', { itemId, destinoId: linhaDestino.idDreLinha })
  draggingId.value = null
}

// --- Helpers ---
const getTextoLista = (lista, codigo) => {
  if (!lista || !codigo) return codigo || '-'
  // Tenta por idListaClienteItem (ex: "DRE_TP_LINHA1")
  let item = lista.find(i => String(i.idListaClienteItem) === String(codigo))
  if (item) return item.textoLista
  // Tenta por idListaSistemaItem (ex: id numérico)
  item = lista.find(i => String(i.idListaSistemaItem) === String(codigo))
  if (item) return item.textoLista
  // Tenta por textoLista (case-insensitive) para valores raw do seed (ex: "ANALITICA")
  item = lista.find(i => i.textoLista && i.textoLista.toUpperCase() === codigo.toUpperCase())
  return item ? item.textoLista : codigo
}

const getTipoBadgeClass = (tipo) => {
  if (!tipo) return 'bg-secondary'
  const t = tipo.toUpperCase()
  if (t === 'ANALITICA') return 'bg-info text-dark'
  if (t === 'SINTETICA') return 'bg-primary'
  if (t === 'TOTALIZADOR') return 'bg-warning text-dark'
  return 'bg-secondary'
}

const getNaturezaBadgeClass = (natureza) => {
  if (!natureza) return 'bg-secondary'
  const n = natureza.toUpperCase()
  if (n === 'RECEITA') return 'bg-success'
  if (n === 'DESPESA') return 'bg-danger'
  if (n === 'TOTALIZADOR') return 'bg-warning text-dark'
  return 'bg-info text-dark'
}
</script>

<template>
  <div v-for="linha in linhas" :key="linha.idDreLinha" class="tree-node-container">
    <!-- Node Row -->
    <div class="tree-node d-flex align-items-center gap-1 py-1 px-2 rounded"
         :class="{
           'bg-body-tertiary': dragOverId === linha.idDreLinha,
           'opacity-50': draggingId === linha.idDreLinha
         }"
         :draggable="!isReadOnly && linha.lstTipoLinha !== 'TOTALIZADOR'"
         @dragstart="onDragStart($event, linha)"
         @dragover="onDragOver($event, linha)"
         @dragleave="onDragLeave($event)"
         @drop="onDrop($event, linha)"
         :style="{ paddingLeft: (nivel * 20) + 'px' }">

      <!-- Expand/Colapse -->
      <button v-if="linha.subLinhas && linha.subLinhas.length > 0"
              class="btn btn-sm p-0 border-0 me-1"
              @click="toggleExpand(linha.idDreLinha)"
              type="button"
              :title="isExpanded(linha.idDreLinha) ? 'Colapsar' : 'Expandir'">
        <i :class="isExpanded(linha.idDreLinha) ? 'bi bi-chevron-down' : 'bi bi-chevron-right'"></i>
      </button>
      <span v-else class="me-1" style="width: 16px;"></span>

      <!-- Código (se houver) -->
      <span v-if="linha.codigoEstruturado" class="badge bg-light text-dark border font-monospace me-1">
        {{ linha.codigoEstruturado }}
      </span>

      <!-- Nome -->
      <span class="flex-grow-1">{{ linha.nome }}</span>

      <!-- Ícone de visibilidade -->
      <span v-if="linha.exibirNoRelatorio" class="text-success me-1" title="Visível no relatório">
        <i class="bi bi-eye-fill"></i>
      </span>
      <span v-else class="text-warning me-1" title="Oculta no relatório">
        <i class="bi bi-eye-slash-fill"></i>
      </span>

      <!-- Badge Tipo -->
      <span class="badge me-1" :class="getTipoBadgeClass(linha.lstTipoLinha)">
        {{ getTextoLista(listas.LstTipoLinha, linha.lstTipoLinha) || linha.lstTipoLinha }}
      </span>

      <!-- Badge Natureza -->
      <span class="badge me-1" :class="getNaturezaBadgeClass(linha.lstNaturezaLinha)">
        {{ getTextoLista(listas.LstNaturezaLinha, linha.lstNaturezaLinha) || linha.lstNaturezaLinha }}
      </span>

      <!-- Categoria vinculada -->
      <small v-if="linha.nomeCategoriaFinanceira" class="text-muted me-2">
        <i class="bi bi-tag me-1"></i>{{ linha.nomeCategoriaFinanceira }}
      </small>

      <!-- Ações -->
      <div class="d-flex gap-1" v-if="!isReadOnly">
        <button type="button" class="btn btn-sm btn-outline-primary py-0 px-1"
                @click="emit('adicionar-filho', { paiId: linha.idDreLinha, nivel: linha.nivel })"
                title="Adicionar sublinha">
          <i class="bi bi-plus-lg"></i>
        </button>
        <button type="button" class="btn btn-sm btn-outline-secondary py-0 px-1"
                @click="emit('editar', linha)"
                title="Editar">
          <i class="bi bi-pencil"></i>
        </button>
        <button type="button" class="btn btn-sm btn-outline-danger py-0 px-1"
                @click="emit('excluir', linha)"
                title="Excluir">
          <i class="bi bi-trash"></i>
        </button>
      </div>
    </div>

    <!-- Sublinhas (recursivo) -->
    <div v-if="linha.subLinhas && linha.subLinhas.length > 0 && isExpanded(linha.idDreLinha)">
      <TreeViewDreLinha
        :linhas="linha.subLinhas"
        :nivel="nivel + 1"
        :isReadOnly="isReadOnly"
        :expandidos="expandidos"
        :listas="listas"
        @update:expandidos="emit('update:expandidos', $event)"
        @adicionar-filho="emit('adicionar-filho', $event)"
        @editar="emit('editar', $event)"
        @excluir="emit('excluir', $event)"
        @mover="emit('mover', $event)"
      />
    </div>
  </div>
</template>

<style scoped>
.tree-node-container {
  border-bottom: 1px solid var(--bs-border-color);
}

.tree-node:hover {
  background-color: var(--bs-tertiary-bg);
}

.tree-node[draggable="true"] {
  cursor: grab;
}

.tree-node[draggable="true"]:active {
  cursor: grabbing;
}
</style>
